import { useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { getToken } from "../api/session";

/* ─── Logo mark (matches LegalPage / LandingPage) ──────────── */
function LogoMark() {
  return (
    <img
      src="./v_two_white.png"
      alt="OnePass Logo"
      style={{ width: 160, height: "auto", objectFit: "contain", display: "block", borderRadius: "10%" }}
    />
  );
}

/* ─── Minimal navbar (reuses the shared nav classes from LegalPage) ── */
function Navbar() {
  const navigate = useNavigate();
  const token = getToken();

  return (
    <nav className="op-nav" style={{ justifyContent: "center", display: "flex" }}>
      <div className="op-nav-inner" style={{ paddingLeft: 24, paddingRight: 16, marginLeft: 16, marginRight: 16, width: "100%" }}>
        <Link to="/" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none", color: "#001645" }}>
          <LogoMark />
        </Link>
        <div className="op-nav-links" style={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Link to="/legal" className="op-nav-link">Legal</Link>
          <Link to="/terms-of-service" className="op-nav-link" style={{ color: "#0055FF", fontWeight: 600 }}>Terms of Service</Link>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {token ? (
            <button onClick={() => navigate("/build")} className="op-btn-primary op-nav-btn" style={{ padding: "11px 22px", fontSize: 14 }}>
              Build Code <ArrowRight size={16} />
            </button>
          ) : (
            <button onClick={() => navigate("/build/login")} className="op-btn-secondary op-nav-btn" style={{ padding: "11px 22px", fontSize: 14 }}>
              Sign In
            </button>
          )}
        </div>
      </div>
    </nav>
  );
}

/* ─── Footer (same as LegalPage) ────────────────────────────── */
function Footer() {
  const links = [
    { label: "Features", to: "/#features" },
    { label: "Pricing", to: "/#pricing" },
    { label: "Legal", to: "/legal" },
    { label: "Terms of Service", to: "/terms-of-service" },
  ];
  return (
    <footer style={{ background: "#fff", borderTop: "1px solid #E5E7EB", padding: "40px 0 20px" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 24, paddingBottom: 28 }}>
          <p style={{ color: "#667085", fontSize: 14, margin: 0 }}>Validated build blueprints for the AI coding era.</p>
          <div style={{ display: "flex", gap: 28, flexWrap: "wrap" }}>
            {links.map((item) => (
              <Link key={item.label} to={item.to} style={{ color: "#667085", textDecoration: "none", fontSize: 14, fontWeight: 500 }}>
                {item.label}
              </Link>
            ))}
          </div>
        </div>
        <div style={{ borderTop: "1px solid #E5E7EB", paddingTop: 18, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <span style={{ color: "#98A2B3", fontSize: 13 }}>© 2026 OnePass. All rights reserved.</span>
        </div>
      </div>
    </footer>
  );
}

/* ─── Terms content ─────────────────────────────────────────── */
const SECTIONS = [
  {
    num: 1,
    title: "What OnePass Blueprint Does",
    body: [
      "OnePass Blueprint is a design, planning, simulation, validation, and project-development system intended to help users transform ideas, requirements, instructions, and other project information into structured blueprints and related project materials before or during implementation.",
      "Depending on the features selected, OnePass may help users:",
    ],
    bullets: [
      "organize and clarify project requirements",
      "identify missing or conflicting requirements",
      "structure application or system designs",
      "analyze meaning, intent, relationships, dependencies, and consequences",
      "perform simulations or “what-if” analysis",
      "identify potential implementation issues",
      "validate portions of a proposed design",
      "create specifications, instructions, acceptance criteria, or project documentation",
      "prepare materials for developers, coding systems, AI systems, or other implementation tools",
      "export or hand off project materials to third-party systems or development environments",
    ],
    footer: [
      "OnePass is intended to improve project planning, understanding, validation, and handoff and to reduce ambiguity before implementation.",
      "OnePass does not guarantee that any project will be error-free, complete, commercially successful, secure, compliant, or suitable for any particular purpose.",
    ],
  },
  {
    num: 2,
    title: "OnePass Is Not the Implementation Provider",
    body: [
      "Unless specifically stated in a separate written agreement, OnePass does not itself provide final software development, engineering, legal, accounting, medical, financial, regulatory, cybersecurity, or other professional services.",
      "A OnePass Blueprint may be provided to a developer, internal team, coding platform, artificial intelligence system, repository, or other third party for implementation.",
      "We do not control and are not responsible for the performance, accuracy, security, availability, actions, outputs, or errors of those third parties.",
      "Users remain responsible for reviewing, testing, approving, deploying, and using any final implementation.",
    ],
  },
  {
    num: 3,
    title: "No Guarantee of Results",
    body: [
      "OnePass uses structured intelligence, analysis, validation, simulation, and other technologies to assist users.",
      "No automated system can identify every possible issue, predict every circumstance, or guarantee the performance of a future implementation.",
      "OnePass does not warrant that:",
    ],
    bullets: [
      "every error will be identified",
      "every requirement will be complete",
      "every conflict or dependency will be detected",
      "every simulation will predict actual results",
      "every recommendation will be appropriate",
      "every Blueprint will be successfully implemented",
      "a developer or third-party coding system will correctly interpret a Blueprint",
      "generated specifications will satisfy every law, regulation, industry standard, security requirement, or business requirement",
    ],
    footer: [
      "Users remain responsible for final review, implementation, testing, deployment, regulatory compliance, security, and use of their projects.",
    ],
  },
  {
    num: 4,
    title: "User Projects and User Content",
    body: [
      "Users may submit ideas, descriptions, requirements, documents, instructions, specifications, files, text, images, and other information to OnePass (“User Content”).",
      "As between the user and OnePass, the user retains ownership of the user’s original User Content.",
      "You grant us only the limited rights reasonably necessary to process User Content in order to operate, provide, secure, maintain, and support the Service and to produce the services and outputs you request.",
      "This limited permission does not transfer ownership of your project, idea, business concept, documents, or other original User Content to OnePass.",
      "We do not use your User Content, project ideas, specifications, or Project Outputs to train generalized artificial-intelligence models unless you expressly authorize us to do so.",
      "You represent that you have the right to submit the User Content and that its use through OnePass does not violate another person’s intellectual-property rights, confidentiality obligations, privacy rights, contractual rights, or applicable law.",
    ],
  },
  {
    num: 5,
    title: "Your Ideas Remain Your Ideas",
    body: [
      "OnePass does not claim ownership of an idea, business concept, application concept, project, invention, or other original material merely because you use OnePass to develop, clarify, simulate, validate, or analyze it.",
      "Your ideas remain your ideas.",
      "You remain responsible for obtaining any patent, trademark, copyright, trade-secret, contractual, confidentiality, or other intellectual-property protection you believe is appropriate.",
      "Use of OnePass by itself does not create patent, copyright, trademark, trade-secret, or other legal protection.",
    ],
  },
  {
    num: 6,
    title: "OnePass Intellectual Property",
    body: [
      "OnePass and its underlying technology remain the proprietary property of AIVenturesPartnersLLC and its licensors.",
      "This includes, without limitation:",
    ],
    bullets: [
      "software and source code",
      "system and backend architecture",
      "the Four-Layer Brain and related intelligence architecture",
      "meaning-processing and meaning-separation architecture",
      "signal systems and signal logic",
      "validation systems",
      "simulation systems",
      "internal logic, algorithms, workflows, decision processes, governance processes",
      "prompts and instruction systems",
      "proprietary specifications, templates and methodologies",
      "automated correction systems, scoring and verification processes",
      "interfaces, designs, databases and data structures, documentation, processes",
      "trademarks, names, branding, and domain assets",
      "improvements, modifications, and derivative technology created by us",
    ],
    footer: [
      "Your use of OnePass does not transfer ownership of any of these technologies to you.",
      "Except where prohibited by law, you may not reverse engineer, extract, reproduce, copy, scrape, reconstruct, decompile, disassemble, sell, sublicense, expose, circumvent, or attempt to discover or recreate OnePass proprietary technology.",
      "Nothing in these Terms grants a user ownership of the technology, architecture, logic, processes, methodologies, or systems used by OnePass to create a Blueprint or other Project Output.",
    ],
  },
  {
    num: 7,
    title: "Blueprint and Project Outputs",
    body: [
      "Subject to these Terms and payment of any applicable charges, users may use the project-specific blueprints, reports, specifications, exports, acceptance criteria, implementation instructions, and similar outputs created for their project (“Project Outputs”).",
      "Project Outputs are intended to be usable by the customer, the customer’s developers, internal teams, designers, coding systems, AI systems, or other implementation resources.",
      "Project Outputs may contain or reflect general structures, formats, methodologies, concepts, patterns, or components also used by OnePass for other projects.",
      "A user’s right to use a Project Output does not give the user ownership of the underlying OnePass platform, Four-Layer Brain, meaning architecture, validators, simulation technology, methodologies, algorithms, internal architecture, or other proprietary technology used to create that output.",
    ],
  },
  {
    num: 8,
    title: "Confidential and Sensitive Information",
    body: [
      "Users should exercise appropriate judgment before submitting highly sensitive information.",
      "Unless a specific OnePass feature or separate written agreement expressly states otherwise, users should not submit information requiring specialized regulated handling without first determining that the Service is appropriate for that information.",
      "Examples may include:",
    ],
    bullets: [
      "medical or protected health information",
      "financial account information",
      "Social Security numbers",
      "passwords or authentication credentials",
      "classified information",
      "regulated personal information",
      "third-party trade secrets",
      "confidential customer records",
      "information the user is not authorized to disclose",
    ],
    footer: ["Users should remove unnecessary sensitive information before submission whenever reasonably possible."],
  },
  {
    num: 9,
    title: "Free Use, Credits, and Paid Services",
    body: [
      "OnePass may provide complimentary Blueprints, trials, promotional credits, paid Blueprint credits, subscriptions, assisted services, or other pricing arrangements.",
      "Current pricing and included services will be displayed through the Service or provided through a separate order, proposal, or agreement.",
      "OnePass may provide a limited number of complimentary Blueprints before additional Blueprint credits, subscriptions, or services must be purchased.",
      "We may modify pricing, credits, packages, features, limits, or promotional offers prospectively. Changes will not retroactively increase charges already paid for completed purchases.",
      "Unless otherwise stated at the time of purchase or required by law, fees are non-refundable once the applicable paid Blueprint, service, or processing has been substantially delivered.",
    ],
  },
  {
    num: 10,
    title: "Assisted or Managed Services",
    body: [
      "OnePass may offer optional assisted, managed, consulting, project-development, validation, or implementation-support services.",
      "Those services may be governed by additional terms, scopes of work, pricing, proposals, or written agreements.",
      "If a separately signed agreement conflicts with these Terms, the signed agreement controls for the services covered by that agreement.",
    ],
  },
  {
    num: 11,
    title: "Third-Party AI, Coding, and Development Systems",
    body: [
      "OnePass may permit users to export, transfer, download, or otherwise provide Project Outputs to third-party products or services. These may include:",
    ],
    bullets: [
      "coding environments",
      "AI coding systems",
      "developer tools",
      "repositories",
      "development platforms",
      "cloud services",
      "other implementation systems",
    ],
    footer: [
      "Those third parties operate independently and under their own terms, privacy policies, security practices, and pricing. OnePass does not control and is not responsible for:",
    ],
    footerBullets: [
      "changes made by a third-party system",
      "third-party interpretation of a Blueprint",
      "third-party use or retention of information",
      "coding or implementation errors",
      "security vulnerabilities introduced during implementation",
      "availability or discontinuation of third-party services",
      "third-party fees or contractual requirements",
      "the performance of code, software, or other products created by those systems",
    ],
    closing: [
      "Use of a OnePass Project Output with a third party does not create a partnership, agency relationship, endorsement, or joint venture between OnePass and that third party unless expressly stated in writing.",
    ],
  },
  {
    num: 12,
    title: "GitHub",
    body: [
      "OnePass may maintain a public GitHub repository, organization, or page for purposes such as product discovery, examples, documentation, testing information, feedback, issue reporting, integration or handoff information, or links to OnePass services.",
      "The public GitHub presence is separate from the proprietary OnePass application and technology.",
      "Publication of information through GitHub does not mean that the OnePass platform, source code, backend architecture, Four-Layer Brain, meaning architecture, signal logic, validators, simulation technology, prompts, proprietary specifications, credentials, databases, or other proprietary intellectual property are open source.",
    ],
  },
];

export default function TermsOfServicePage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <>
      <style>{`
        :root {
          --op-blue: #0055FF; --op-heading: #001645; --op-border: #E5E7EB;
          --op-grad: linear-gradient(93deg,#0D47A1 0%,#0055FF 50%,#001645 100%);
        }
        *, *::before, *::after { box-sizing: border-box; }
        body { margin: 0; font-family: 'Inter', system-ui, sans-serif; }
        .op-nav { position: static; padding: 14px 0; }
        .op-nav-inner { display:flex; align-items:center; justify-content:space-between; padding:14px 28px; margin:0 auto; max-width:1200px; border-radius:20px; background:#fff; border:1px solid var(--op-border); }
        .op-nav-link { font-size:14px; font-weight:500; color:#3a4a6b; text-decoration:none; padding:8px 12px; border-radius:10px; }
        .op-nav-link:hover { color:var(--op-blue); background:rgba(0,85,255,.06); }
        .op-btn-primary { border:none; background:var(--op-grad); color:#fff; font-family:inherit; font-weight:600; border-radius:14px; display:inline-flex; align-items:center; gap:8px; cursor:pointer; }
        .op-btn-secondary { background:#fff; color:var(--op-heading); border:1px solid var(--op-border); font-family:inherit; font-weight:600; border-radius:14px; cursor:pointer; }
        .tos-section + .tos-section { margin-top: 32px; }
        .tos-section h2 { font-size: 18px; font-weight: 800; color: #001645; margin: 0 0 12px; }
        .tos-section p { font-size: 14.5px; color: #4B5563; line-height: 1.8; margin: 0 0 12px; }
        .tos-section ul { margin: 0 0 12px; padding-left: 22px; }
        .tos-section li { font-size: 14.5px; color: #4B5563; line-height: 1.8; margin-bottom: 4px; }
      `}</style>

      <div style={{ background: "#F8FAFF", minHeight: "100vh" }}>
        <Navbar />

        <div style={{ maxWidth: 820, margin: "0 auto", padding: "48px 24px 80px" }}>
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "#0055FF", background: "rgba(0,85,255,.09)", border: "1px solid rgba(0,85,255,.18)", borderRadius: 20, padding: "5px 14px", marginBottom: 16 }}>
              <ShieldCheck size={12} /> Legal
            </span>
            <h1 style={{ fontSize: "clamp(26px,5vw,40px)", fontWeight: 900, color: "#001645", margin: "0 0 8px" }}>
              OnePass Blueprint Terms of Service
            </h1>
            <p style={{ color: "#667085", fontSize: 14, margin: 0 }}>Effective Date: September 8, 2026</p>
          </div>

          <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 20, padding: "36px 32px", boxShadow: "0 4px 16px rgba(0,22,69,.05)" }}>
            <p style={{ fontSize: 14.5, color: "#4B5563", lineHeight: 1.8 }}>
              These Terms of Service (“Terms”) govern access to and use of the OnePass Blueprint website, software, platform, services, tools, features, and related offerings (collectively, “OnePass” or the “Service”).
            </p>
            <p style={{ fontSize: 14.5, color: "#4B5563", lineHeight: 1.8 }}>
              OnePass is operated by <strong>AIVenturesPartnersLLC</strong> (“Company,” “we,” “us,” or “our”).
            </p>
            <p style={{ fontSize: 14.5, color: "#4B5563", lineHeight: 1.8, marginBottom: 0 }}>
              By accessing or using OnePass, you agree to these Terms. If you do not agree, do not use the Service.
            </p>

            {SECTIONS.map((s) => (
              <div key={s.num} className="tos-section" style={{ borderTop: "1px solid #F1F5F9", paddingTop: 28, marginTop: 28 }}>
                <h2>{s.num}. {s.title.toUpperCase()}</h2>
                {s.body.map((p, i) => <p key={i}>{p}</p>)}
                {s.bullets && (
                  <ul>
                    {s.bullets.map((b, i) => <li key={i}>{b}</li>)}
                  </ul>
                )}
                {s.footer && s.footer.map((p, i) => <p key={i}>{p}</p>)}
                {s.footerBullets && (
                  <ul>
                    {s.footerBullets.map((b, i) => <li key={i}>{b}</li>)}
                  </ul>
                )}
                {s.closing && s.closing.map((p, i) => <p key={i}>{p}</p>)}
              </div>
            ))}
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}
